import { getFirebaseBackend } from "../../../helpers/firebase_helper";
import {
  postFakeLogin,
  postJwtLogin,
  postJwtLogout,
  postJwtGoogleAuth,
} from "../../../helpers/fakebackend_helper";
import {
  clearAuthorization,
  persistTenantSession,
} from "../../../helpers/api_helper";
import {
  getStoredDeviceTrustToken,
  storeDeviceTrustToken,
} from "../../../helpers/mfa_helper";
import { normalizeLocale } from "../../../Components/Common/AuthLocaleBar";
import { isIntlPortalHost } from "../../../helpers/brandPortal";

import { loginSuccess, logoutUserSuccess, apiError, reset_login_flag } from './reducer';

/** Alias local — mesmo helper do register (DOC 06 § 5.1). */
const persistSession = (data: any) => persistTenantSession(data);

/**
 * Continuar com Google — login, link ou signup se e-mail novo (LAC-045).
 * Sem challenge MFA: o 2º fator fica na conta Google.
 */
export const loginWithGoogle =
  (
    idToken: string,
    history: any,
    extras?: {
      company_name?: string;
      referral_code?: string;
      preferred_billing_currency?: "BRL" | "USD";
    }
  ) =>
  async (dispatch: any) => {
    try {
      const locale = normalizeLocale(
        localStorage.getItem("I18N_LANGUAGE") || undefined
      );
      const preferred =
        extras?.preferred_billing_currency ||
        (isIntlPortalHost() ? "USD" : "BRL");

      const envelope = await postJwtGoogleAuth({
        id_token: idToken,
        locale: locale ?? undefined,
        preferred_billing_currency: preferred,
        referral_code: extras?.referral_code,
        company_name: extras?.company_name,
      });

      if (!envelope?.success || !envelope?.data?.access_token) {
        dispatch(
          apiError(
            envelope?.error?.message ||
              "Não foi possível entrar com o Google. Tente de novo."
          )
        );
        return;
      }

      persistSession(envelope.data);
      dispatch(loginSuccess(envelope.data));

      // Signup novo via Google → wizard empresa + senha (fallback).
      if (envelope.data.created) {
        history("/onboarding/google", {
          replace: true,
          state: {
            requiresCheckout: !!envelope.data.requires_checkout,
          },
        });
        return;
      }

      if (envelope.data.requires_checkout) {
        history("/settings/billing", {
          state: { requiresCheckout: true },
        });
        return;
      }

      history("/dashboard");
    } catch (error: any) {
      dispatch(
        apiError(
          error?.message ||
            "Não foi possível entrar com o Google. Tente de novo."
        )
      );
    }
  };

export const loginUser = (user: any, history: any) => async (dispatch: any) => {
  try {
    let response: any;

    if (process.env.REACT_APP_DEFAULTAUTH === "firebase") {
      const fireBaseBackend: any = getFirebaseBackend();
      response = await fireBaseBackend.loginUser(user.email, user.password);
      persistSession(response);
      dispatch(loginSuccess(response));
      history("/dashboard");
      return;
    }

    if (process.env.REACT_APP_DEFAULTAUTH === "jwt") {
      // api_helper já devolve o body JSON (envelope) em 2xx;
      // 4xx cai no catch com { message, code }.
      try {
        const envelope = await postJwtLogin({
          email: user.email,
          password: user.password,
          device_trust_token: getStoredDeviceTrustToken() || undefined,
        });

        if (!envelope?.success) {
          dispatch(
            apiError(
              envelope?.error?.message ||
                "E-mail ou senha incorretos. Tente de novo."
            )
          );
          return;
        }

        // MFA ligado: guarda challenge + expiração p/ countdown na UI.
        if (envelope?.data?.mfa_required && envelope?.data?.mfa_token) {
          // Token de device trust inválido/expirado — limpa para não reenviar lixo.
          storeDeviceTrustToken(null);
          sessionStorage.setItem(
            "mfa_challenge_token",
            envelope.data.mfa_token
          );
          const expiresIn = Number(envelope.data.expires_in) || 300;
          sessionStorage.setItem(
            "mfa_challenge_expires_at",
            String(Date.now() + expiresIn * 1000)
          );
          if (envelope.data.mfa_method) {
            sessionStorage.setItem(
              "mfa_challenge_method",
              String(envelope.data.mfa_method)
            );
          } else {
            sessionStorage.removeItem("mfa_challenge_method");
          }
          if (envelope.data.email_hint) {
            sessionStorage.setItem(
              "mfa_challenge_email_hint",
              String(envelope.data.email_hint)
            );
          } else {
            sessionStorage.removeItem("mfa_challenge_email_hint");
          }
          history("/login/mfa");
          return;
        }

        if (!envelope?.data?.access_token) {
          dispatch(
            apiError(
              envelope?.error?.message ||
                "E-mail ou senha incorretos. Tente de novo."
            )
          );
          return;
        }

        persistSession(envelope.data);
        dispatch(loginSuccess(envelope.data));
        history("/dashboard");
      } catch (error: any) {
        dispatch(
          apiError(
            error?.message ||
              "E-mail ou senha incorretos. Tente de novo."
          )
        );
      }
      return;
    }

    if (process.env.REACT_APP_DEFAULTAUTH) {
      response = await postFakeLogin({
        email: user.email,
        password: user.password,
      });

      sessionStorage.setItem("authUser", JSON.stringify(response));
      if (process.env.REACT_APP_DEFAULTAUTH === "fake") {
        let finallogin: any = response;
        if (typeof finallogin === "string") {
          finallogin = JSON.parse(finallogin);
        }
        const data = finallogin.data;
        if (finallogin.status === "success") {
          dispatch(loginSuccess(data));
          history("/dashboard");
        } else {
          dispatch(apiError(finallogin));
        }
      } else {
        dispatch(loginSuccess(response));
        history("/dashboard");
      }
    }
  } catch (error: any) {
    dispatch(apiError(error?.message || error));
  }
};

export const logoutUser = () => async (dispatch: any) => {
  try {
    const raw = sessionStorage.getItem("authUser");
    let refreshToken: string | undefined;

    if (raw) {
      try {
        refreshToken = JSON.parse(raw)?.refresh_token;
      } catch {
        // ignore
      }
    }

    if (process.env.REACT_APP_DEFAULTAUTH === "jwt") {
      try {
        await postJwtLogout(refreshToken ? { refresh_token: refreshToken } : undefined);
      } catch {
        // Mesmo se a API falhar, limpa a sessão local.
      }
    }

    sessionStorage.removeItem("authUser");
    clearAuthorization();

    if (process.env.REACT_APP_DEFAULTAUTH === "firebase") {
      const fireBaseBackend: any = getFirebaseBackend();
      dispatch(logoutUserSuccess(fireBaseBackend.logout));
    } else {
      dispatch(logoutUserSuccess(true));
    }
  } catch (error: any) {
    dispatch(apiError(error?.message || error));
  }
};

export const socialLogin = (type: any, history: any) => async (dispatch: any) => {
  try {
    let response;

    if (process.env.REACT_APP_DEFAULTAUTH === "firebase") {
      const fireBaseBackend: any = getFirebaseBackend();
      response = fireBaseBackend.socialLoginUser(type);
    }

    const socialdata = await response;
    if (socialdata) {
      sessionStorage.setItem("authUser", JSON.stringify(response));
      dispatch(loginSuccess(response));
      history("/dashboard");
    }
  } catch (error: any) {
    dispatch(apiError(error?.message || error));
  }
};

export const resetLoginFlag = () => async (dispatch: any) => {
  try {
    return dispatch(reset_login_flag());
  } catch (error: any) {
    dispatch(apiError(error?.message || error));
  }
};
